import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import Sidebar from './Sidebar';
import Header from './Header';
import CultureSettings from './CultureSettings';
import ConfirmationModal from './ConfirmationModal';
import './Settings.css';
import {
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  CloudDone as CloudDoneIcon,
  CloudOff as CloudOffIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon
} from '@mui/icons-material';

interface GoogleClassroomStatus {
  connected: boolean;
  needs_refresh: boolean;
  has_tokens: boolean;
}

const Settings: React.FC = () => {
  const { t } = useTranslation();
  const { token, isGlobalAdmin, isOrgAdmin } = useAuth();
  const [isResetting, setIsResetting] = useState(false);
  const [resetMessage, setResetMessage] = useState<string | null>(null);
  const [resetError, setResetError] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  
  // Google Classroom state
  const [googleClassroomStatus, setGoogleClassroomStatus] = useState<GoogleClassroomStatus>({
    connected: false,
    needs_refresh: false,
    has_tokens: false
  });
  const [isLoadingGoogleStatus, setIsLoadingGoogleStatus] = useState(true);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [googleMessage, setGoogleMessage] = useState<string | null>(null);
  const [googleError, setGoogleError] = useState<string | null>(null);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);

  // Load Google Classroom status on component mount
  useEffect(() => {
    if (token) {
      checkGoogleClassroomStatus();
    }
  }, [token]);

  const checkGoogleClassroomStatus = async () => {
    try {
      setIsLoadingGoogleStatus(true);
      
      const response = await fetch('/api/auth/google-classroom/status', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setGoogleClassroomStatus(data);
      }
    } catch (error: any) {
      console.error('Error checking Google Classroom status:', error);
      setGoogleError(t('settings.google.errors.checkStatusFailed'));
    } finally {
      setIsLoadingGoogleStatus(false);
    }
  };

  const handleGoogleConnect = async () => {
    try {
      setGoogleError(null);
      
      const response = await fetch('/api/auth/google-classroom/connect', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (data.success && data.authorization_url) {
        // Redirect user to Google OAuth
        window.location.href = data.authorization_url;
      } else {
        throw new Error(data.error || t('settings.google.errors.initiateOauthFailed'));
      }
      
    } catch (error: any) {
      setGoogleError(error.message || t('settings.google.errors.connectFailed'));
    }
  };

  const handleGoogleDisconnectClick = () => {
    setShowDisconnectModal(true);
  };

  const handleGoogleDisconnect = async () => {
    setShowDisconnectModal(false);
    
    try {
      setIsDisconnecting(true);
      setGoogleError(null);
      setGoogleMessage(null);

      const response = await fetch('/api/auth/google-classroom/disconnect', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        setGoogleMessage(t('settings.google.messages.disconnectedSuccess'));
        // Refresh the status
        await checkGoogleClassroomStatus();
        setTimeout(() => setGoogleMessage(null), 8000); // Clear after 8 seconds
      } else {
        throw new Error(data.error || t('settings.google.errors.disconnectFailed'));
      }

    } catch (error: any) {
      setGoogleError(error.message || t('settings.google.errors.disconnectFailed'));
      setTimeout(() => setGoogleError(null), 10000); // Clear after 10 seconds
    } finally {
      setIsDisconnecting(false);
    }
  };

  const handleDemoResetClick = () => {
    setShowConfirmModal(true);
  };

  const handleDemoReset = async () => {
    setShowConfirmModal(false);

    try {
      setIsResetting(true);
      setResetError(null);
      setResetMessage(null);

      const response = await fetch('/api/google-classroom/demo/reset-imports', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        setResetMessage(t('settings.demo.messages.resetSuccess', { message: data.message }));
        setTimeout(() => setResetMessage(null), 10000); // Clear after 10 seconds
      } else {
        throw new Error(data.error || t('settings.demo.errors.resetFailed'));
      }

    } catch (error: any) {
      setResetError(error.message || t('settings.demo.errors.resetFailed'));
      setTimeout(() => setResetError(null), 10000); // Clear after 10 seconds
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className="settings">
      <Sidebar />
      <div className="main-content">
        <Header />
        <div className="content-area">
          <div className="settings-container">
            <div className="settings-content">
              <h1>{t('settings.pageTitle')}</h1>
              <p>{t('settings.pageSubtitle')}</p>
              
              {/* Culture Settings Section - Available to all users */}
              <CultureSettings />

              {/* Google Classroom Integration Section */}
              <div className="settings-section">
                <div className="settings-card google-classroom-section">
                  <h3>
                    {googleClassroomStatus.connected ? (
                      <CloudDoneIcon style={{ color: '#10b981', marginRight: '8px' }} />
                    ) : (
                      <CloudOffIcon style={{ color: '#6b7280', marginRight: '8px' }} />
                    )}
                    {t('settings.google.title')}
                  </h3>
                  <p>{t('settings.google.description')}</p>
                  
                  {googleMessage && (
                    <div className="demo-message success">
                      <CheckCircleIcon />
                      {googleMessage}
                    </div>
                  )}
                  
                  {googleError && (
                    <div className="demo-message error">
                      <ErrorIcon />
                      {googleError}
                    </div>
                  )}
                  
                  <div className="google-classroom-status">
                    {isLoadingGoogleStatus ? (
                      <div className="status-loading">
                        <RefreshIcon className="spinning" />
                        {t('settings.google.checkingStatus')}
                      </div>
                    ) : (
                      <>
                        <div className={`connection-status ${googleClassroomStatus.connected ? 'connected' : 'disconnected'}`}>
                          {googleClassroomStatus.connected ? (
                            <>
                              <CheckCircleIcon />
                              <span>{t('settings.google.status.connected')}</span>
                            </>
                          ) : (
                            <>
                              <CloudOffIcon />
                              <span>{t('settings.google.status.notConnected')}</span>
                            </>
                          )}
                        </div>
                        
                        {googleClassroomStatus.needs_refresh && (
                          <div className="status-warning">
                            <WarningIcon />
                            {t('settings.google.status.needsRefresh')}
                          </div>
                        )}
                        
                        <div className="google-actions">
                          {googleClassroomStatus.connected ? (
                            <>
                              <button
                                onClick={checkGoogleClassroomStatus}
                                disabled={isLoadingGoogleStatus}
                                className="btn-refresh"
                                title={t('settings.google.actions.refreshStatusTitle')}
                                aria-label={t('settings.google.actions.refreshStatusAria')}
                              >
                                <RefreshIcon />
                                {t('settings.google.actions.refreshStatus')}
                              </button>
                              <button
                                onClick={handleGoogleDisconnectClick}
                                disabled={isDisconnecting}
                                className="btn-disconnect"
                                aria-label={t('settings.google.actions.disconnectAria')}
                              >
                                <LinkOffIcon />
                                {isDisconnecting ? t('settings.google.actions.disconnecting') : t('settings.google.actions.disconnect')}
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={handleGoogleConnect}
                              className="btn-connect"
                              aria-label={t('settings.google.actions.connectAria')}
                            >
                              <LinkIcon />
                              {t('settings.google.actions.connect')}
                            </button>
                          )}
                        </div>
                        
                        {googleClassroomStatus.connected && (
                          <div className="connection-info">
                            <p style={{ fontSize: '0.875rem', color: '#666', marginTop: '12px' }}>
                              <strong>{t('settings.google.whatYouCanDoTitle')}</strong> {t('settings.google.whatYouCanDoBody')}
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Demo Settings Section */}
              <div className="settings-section">
                <div className="settings-card demo-section">
                  <h3>
                    <WarningIcon style={{ color: '#f59e0b', marginRight: '8px' }} />
                    {t('settings.demo.title')}
                  </h3>
                  <p>{t('settings.demo.description')}</p>
                  
                  {resetMessage && (
                    <div className="demo-message success">
                      <CheckCircleIcon />
                      {resetMessage}
                    </div>
                  )}
                  
                  {resetError && (
                    <div className="demo-message error">
                      <ErrorIcon />
                      {resetError}
                    </div>
                  )}
                  
                  <div className="demo-reset-section">
                    <h4>{t('settings.demo.resetSection.title')}</h4>
                    <p>
                      {t('settings.demo.resetSection.body')}
                    </p>
                    <p style={{ fontSize: '0.875rem', color: '#666' }}>
                      <strong>{t('settings.demo.resetSection.noteTitle')}</strong> {t('settings.demo.resetSection.noteBody')}
                    </p>
                    
                    <button
                      onClick={handleDemoResetClick}
                      disabled={isResetting}
                      className="btn-demo-reset"
                      aria-label={t('settings.demo.resetSection.resetButtonAria')}
                    >
                      <RefreshIcon />
                      {isResetting ? t('settings.demo.resetSection.resetting') : t('settings.demo.resetSection.resetButton')}
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Placeholder content - ready for future implementation */}
              <div className="settings-placeholder">
                <div className="placeholder-card">
                  <h3>{t('settings.accountSettings')}</h3>
                  <p>{t('settings.accountSettingsDesc')}</p>
                </div>
                
                <div className="placeholder-card">
                  <h3>{t('settings.classroomSettings')}</h3>
                  <p>{t('settings.classroomSettingsDesc')}</p>
                </div>
                
                <div className="placeholder-card">
                  <h3>{t('settings.notificationSettings')}</h3>
                  <p>{t('settings.notificationSettingsDesc')}</p>
                </div>
                
                <div className="placeholder-card">
                  <h3>{t('settings.privacySecurity')}</h3>
                  <p>{t('settings.privacySecurityDesc')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Demo Reset Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        title={t('settings.demo.confirmReset.title')}
        message={t('settings.demo.confirmReset.message')}
        variant="warning"
        confirmText={t('settings.demo.confirmReset.confirmText')}
        onConfirm={handleDemoReset}
        onCancel={() => setShowConfirmModal(false)}
        isLoading={isResetting}
      />

      {/* Google Classroom Disconnect Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDisconnectModal}
        title={t('settings.google.confirmDisconnect.title')}
        message={t('settings.google.confirmDisconnect.message')}
        variant="warning"
        confirmText={t('settings.google.confirmDisconnect.confirmText')}
        onConfirm={handleGoogleDisconnect}
        onCancel={() => setShowDisconnectModal(false)}
        isLoading={isDisconnecting}
      />
    </div>
  );
};

export default Settings;
